import { z } from 'zod'

import { StudentIdSchema } from '~/@entities/Student'
import type {
  CommonCloudFrontUrlResponse,
  CommonResponseListMonthlyAvailableReportResponse,
  CommonResponseListReportResponse,
  CommonResponseRecentlyReportResponse,
  CommonResponseReportCreateResponse,
  CommonResponseReportResponse,
  CommonResponseSetString,
  CommonResponseStudentAchievementReportResponse,
  CustomReportResponse,
  CustomReportScoringHistoryCountResponse,
  CustomReportScoringHistoryResponse,
  RealTimeReportResponse,
  RealTimeReportSettingResponse,
  ReportLinkResponse,
  ReportMetricResponse,
  ReportScoringGenerationResponse,
  ReportScoringWithSettingGenerationResponse,
  SliceCustomReportResponse,
} from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'
import { type Slice, sliceSchema } from '~/schema/slice'

import {
  type CustomReport,
  type CustomReportEditSource,
  CustomReportEditSourceSchema,
  CustomReportSchema,
  type LegacyReport,
  type LegacyReportSaveResult,
  LegacyReportSaveResultSchema,
  LegacyReportSchema,
  type MonthlyReportAvailability,
  MonthlyReportAvailabilitySchema,
  type RealTimeReport,
  RealTimeReportSchema,
  type RealTimeReportSetting,
  RealTimeReportSettingSchema,
  type RecentReportYearMonth,
  RecentReportYearMonthSchema,
  type ReportAiComment,
  ReportAiCommentSchema,
  type ReportAiSession,
  ReportAiSessionSchema,
  type ReportDraft,
  ReportDraftSchema,
  type ReportFile,
  ReportFileSchema,
  type ReportMetric,
  ReportMetricSchema,
  type ReportScoringHistory,
  type ReportScoringHistoryCount,
  ReportScoringHistoryCountSchema,
  ReportScoringHistorySchema,
  type ReportShareLink,
  ReportShareLinkSchema,
  type StudentAchievementReports,
  StudentAchievementReportsSchema,
} from './schemas'

const ENTITY = 'Report'

// 스펙은 보고서 본문(ReportRequestInterface)과 목록·설정의 nullable 필드(logoUrl·comparisonType·commentComponent·
// reportIsOpen·metric 의 세 수치·behaviorAreaAverageScore 등)를 optional 로만 그린다. 서버는 null 을 직렬화한다.
// 또 채점 이력의 Workbook·Cas·Worksheet 를 이름이 같은 다른 응답의 클래스로 덮어썼다(교재 이력의 studentWorkbookId·
// 자동시험 이력의 assignedCasId 가 스펙에 없다). 2026-10-07 백엔드 develop 의 report/model/dto/
// ReportRequestInterface.kt·report/controller/response/*.kt 로 확인.
const asWire = (raw: unknown): unknown => raw

export const adaptRealTimeReports = (raw: RealTimeReportResponse[]): RealTimeReport[] =>
  parseApiResponse(z.array(RealTimeReportSchema), raw, ENTITY)

export const adaptCustomReports = (raw: SliceCustomReportResponse): Slice<CustomReport> =>
  parseApiResponse(sliceSchema(CustomReportSchema), asWire(raw), ENTITY)

export const adaptCustomReportList = (raw: CustomReportResponse[]): CustomReport[] =>
  parseApiResponse(z.array(CustomReportSchema), asWire(raw), ENTITY)

export const adaptReportMetric = (raw: ReportMetricResponse): ReportMetric =>
  parseApiResponse(ReportMetricSchema, raw, ENTITY)

export const adaptRealTimeReportSetting = (
  raw: RealTimeReportSettingResponse,
): RealTimeReportSetting => parseApiResponse(RealTimeReportSettingSchema, asWire(raw), ENTITY)

export const adaptReportScoringHistory = (
  raw: CustomReportScoringHistoryResponse,
): ReportScoringHistory => parseApiResponse(ReportScoringHistorySchema, asWire(raw), ENTITY)

export const adaptReportScoringHistoryCount = (
  raw: CustomReportScoringHistoryCountResponse,
): ReportScoringHistoryCount => parseApiResponse(ReportScoringHistoryCountSchema, raw, ENTITY)

export const adaptReportDraft = (raw: ReportScoringGenerationResponse): ReportDraft =>
  parseApiResponse(ReportDraftSchema, asWire(raw), ENTITY)

export const adaptCustomReportEditSource = (
  raw: ReportScoringWithSettingGenerationResponse,
): CustomReportEditSource => parseApiResponse(CustomReportEditSourceSchema, asWire(raw), ENTITY)

export const adaptReportShareLink = (raw: ReportLinkResponse): ReportShareLink =>
  parseApiResponse(ReportShareLinkSchema, raw, ENTITY)

export const adaptReportFile = (raw: CommonCloudFrontUrlResponse): ReportFile =>
  parseApiResponse(ReportFileSchema, raw, ENTITY)

/***************** 구 보고서 *****************/
// 스펙은 서버 YearMonth 를 객체로, ReportResponse 의 nullable 필드(chapter·behaviorScore·totalScore·totalTier·pdfUrl·
// recreatedStudentIds 등)를 optional 로만 그린다. 서버는 yyyy-MM 문자열과 null 을 직렬화한다.
// 2026-10-13 백엔드 develop 의 report/controller/response/ReportResponse.kt·ReportCreateResponse.kt 로 확인.

export const adaptLegacyReports = (raw: CommonResponseListReportResponse['data']): LegacyReport[] =>
  parseApiResponse(z.array(LegacyReportSchema), asWire(raw), ENTITY)

export const adaptLegacyReport = (raw: CommonResponseReportResponse['data']): LegacyReport =>
  parseApiResponse(LegacyReportSchema, asWire(raw), ENTITY)

export const adaptMonthlyReportAvailabilities = (
  raw: CommonResponseListMonthlyAvailableReportResponse['data'],
): MonthlyReportAvailability[] =>
  parseApiResponse(z.array(MonthlyReportAvailabilitySchema), asWire(raw), ENTITY)

export const adaptLegacyReportSaveResult = (
  raw: CommonResponseReportCreateResponse['data'],
): LegacyReportSaveResult => parseApiResponse(LegacyReportSaveResultSchema, asWire(raw), ENTITY)

export const adaptRecentReportYearMonth = (
  raw: CommonResponseRecentlyReportResponse['data'],
): RecentReportYearMonth => parseApiResponse(RecentReportYearMonthSchema, raw, ENTITY)

export const adaptReportStudentIds = (
  raw: CommonResponseSetString['data'],
): LegacyReport['studentId'][] => parseApiResponse(z.array(StudentIdSchema), raw, ENTITY)

/***************** 유형분석 보고서 *****************/
// 스펙이 Kotlin nullable 을 optional 로만 그린 곳. creverseShared 는 크레버스가 아닌 학원에서 null 로 온다.
// 2026-10-13 백엔드 develop(cc5f60e17) 의 report/controller/response/StudentAchievementReportResponse.kt 로 확인.

export const adaptStudentAchievementReports = (
  raw: CommonResponseStudentAchievementReportResponse['data'],
): StudentAchievementReports => parseApiResponse(StudentAchievementReportsSchema, raw, ENTITY)

/***************** AI 코멘트 (AI 서버) *****************/
// 요청 함수는 AI 서버 전용 클라이언트를 가진 앱에 있다. 생성 타입이 없어 응답 본문을 unknown 으로 받는다.

export const adaptReportAiSession = (raw: unknown): ReportAiSession =>
  parseApiResponse(ReportAiSessionSchema, raw, ENTITY)

export const adaptReportAiComment = (raw: unknown): ReportAiComment =>
  parseApiResponse(ReportAiCommentSchema, raw, ENTITY)
